import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Wizard } from "./Wizard";
import { ShopFinder } from "./ShopFinder";
import { Landing, type LandingLoc, type LandingStats } from "./Landing";
import { getShops, listInsurers } from "./api";
import { SPECIAL_IDS } from "./insurers";
import type { Insurer, ShopsResponse } from "../shared/types";
import type { SavedWizard } from "./persist";
import type { DriveHooks } from "./garage/engine";

type Screen = "landing" | "finder" | "safety" | "wizard";

type Prefill = { insurerId: string; zip: string | null; lat: number | null; lng: number | null };

// The listings key a context resolves to — the map reuses the garage's
// prefetch only when this matches exactly.
const shopsKey = (insurerId: string, zip: string | null, lat: number | null, lng: number | null) =>
  `${SPECIAL_IDS.has(insurerId) ? "-" : insurerId}|${lat != null && lng != null ? `${lat},${lng}` : (zip ?? "none")}`;

export function App() {
  const [screen, setScreen] = useState<Screen>("landing");
  const [consented, setConsented] = useState(false);
  const [resume, setResume] = useState<SavedWizard | null>(null);
  const [prefill, setPrefill] = useState<Prefill | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // The landing IS the garage: the menu rows are the questions.
  const [insurers, setInsurers] = useState<Insurer[]>([]);
  const [insurersErr, setInsurersErr] = useState(false);
  const [insurerId, setInsurerId] = useState<string | null>(null);
  const [loc, setLoc] = useState<LandingLoc>({ zip: null, coords: null, decided: false });
  const [openPanel, setOpenPanel] = useState<{ panel: "insurer" | "loc"; nonce: number } | null>(null);

  const loadInsurers = useCallback(() => {
    setInsurersErr(false);
    listInsurers()
      .then(setInsurers)
      .catch(() => setInsurersErr(true));
  }, []);
  useEffect(loadInsurers, [loadInsurers]);

  // Once both questions are answered, fetch the listings right away: they
  // fill the garage's stat bars, and the map then draws instantly when the
  // car drives out. Same request the map would make, just earlier.
  const [shops, setShops] = useState<{ key: string; data: ShopsResponse | null; loading: boolean } | null>(
    null,
  );
  useEffect(() => {
    if (!insurerId || !loc.decided) return;
    const lat = loc.coords?.lat ?? null;
    const lng = loc.coords?.lng ?? null;
    const key = shopsKey(insurerId, loc.zip, lat, lng);
    if (shops?.key === key) return;
    setShops({ key, data: null, loading: true });
    // Warm the map chunk too, so it can mount under the drive-out at once.
    void import("./Shops");
    getShops({ insurerId: SPECIAL_IDS.has(insurerId) ? null : insurerId, lat, lng, zip: loc.zip })
      .then((data) => setShops((s) => (s?.key === key ? { key, data, loading: false } : s)))
      .catch(() => setShops((s) => (s?.key === key ? { key, data: null, loading: false } : s)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insurerId, loc]);

  const stats: LandingStats | null = useMemo(() => {
    const d = shops?.data;
    if (!d || !insurerId) return null;
    const isRepair = (k?: string) => k === "repair" || k === undefined;
    const repair = d.shops.filter((s) => isRepair(s.kind) && !s.beyondNearest);
    const near = repair.reduce<number | null>(
      (m, s) => (s.distanceMiles !== undefined && (m === null || s.distanceMiles < m) ? s.distanceMiles : m),
      null,
    );
    return {
      repair: repair.length,
      network: SPECIAL_IDS.has(insurerId) ? null : d.shops.filter((s) => isRepair(s.kind) && s.inNetwork).length,
      tow: d.shops.filter((s) => s.kind === "tow").length,
      salvage: d.shops.filter((s) => s.kind === "salvage").length,
      nearest: near === null ? null : Math.round(near * 10) / 10,
    };
  }, [shops, insurerId]);

  // ---- drive-out: the garage overlay and the map are mounted together; the
  // car drives onto the user's spot, then the overlay goes.
  const [driving, setDriving] = useState(false);
  const [introArrived, setIntroArrived] = useState(true);
  const intro = useRef<{ ready: boolean; point: { x: number; y: number } | null }>({ ready: true, point: null });
  const [garageLive, setGarageLive] = useState(false);
  const driveHooks = useMemo<DriveHooks>(
    () => ({
      mapReady: () => intro.current.ready,
      target: () => intro.current.point,
      onArrive: () => setIntroArrived(true),
      onDone: () => setDriving(false),
    }),
    [],
  );
  const onMapReady = useCallback((point: { x: number; y: number } | null) => {
    intro.current = { ready: true, point };
  }, []);

  const go = () => {
    if (!insurerId || !loc.decided) return;
    setPrefill({ insurerId, zip: loc.zip, lat: loc.coords?.lat ?? null, lng: loc.coords?.lng ?? null });
    setResume(null);
    setNotice(null);
    setOpenPanel(null);
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    if (garageLive && !reduce) {
      intro.current = { ready: false, point: null };
      setIntroArrived(false);
      setDriving(true);
      // Never strand the car: if the map can't report in, go anyway.
      setTimeout(() => {
        if (!intro.current.ready) intro.current = { ready: true, point: null };
      }, 6000);
    } else {
      setIntroArrived(true);
    }
    setScreen("finder");
  };

  // The landing garage is full-screen and never scrolls (its long carrier
  // list scrolls inside the menu).
  useEffect(() => {
    const lock = screen === "landing" || driving;
    document.documentElement.classList.toggle("landing-lock", lock);
    return () => document.documentElement.classList.remove("landing-lock");
  }, [screen, driving]);

  const prefillInsurer = prefill ? (insurers.find((i) => i.id === prefill.insurerId) ?? null) : null;
  const prefillKey = prefill ? shopsKey(prefill.insurerId, prefill.zip, prefill.lat, prefill.lng) : null;

  return (
    <main className="shell">
      {/* Kept mounted (paused) behind the map so ⌂ returns to it instantly. */}
      {(screen === "landing" || screen === "finder") && (
        <Landing
          visible={screen === "landing" || driving}
          driving={driving}
          driveHooks={driveHooks}
          onLive={setGarageLive}
          insurers={insurers}
          insurersErr={insurersErr}
          onRetryInsurers={loadInsurers}
          insurerId={insurerId}
          onInsurer={setInsurerId}
          loc={loc}
          onLoc={(next) => setLoc({ ...next, decided: true })}
          stats={stats}
          statsLoading={Boolean(shops?.loading)}
          openPanel={openPanel}
          notice={notice}
          onGo={go}
        />
      )}

      {screen === "finder" && prefill && (
        <ShopFinder
          insurer={prefillInsurer}
          insurerId={prefill.insurerId}
          zip={prefill.zip}
          coords={prefill.lat != null && prefill.lng != null ? { lat: prefill.lat, lng: prefill.lng } : null}
          initialData={shops?.key === prefillKey ? (shops?.data ?? null) : null}
          intro={{ arrived: introArrived, onReady: onMapReady }}
          onExit={() => {
            setOpenPanel(null);
            setScreen("landing");
          }}
          onChange={(panel) => {
            setOpenPanel({ panel, nonce: Date.now() });
            setScreen("landing");
          }}
          onEstimate={(id, zip, lat, lng) => {
            setPrefill({ insurerId: id, zip, lat, lng });
            setResume(null);
            setNotice(null);
            setScreen("safety");
          }}
        />
      )}

      {screen === "safety" && (
        <section className="card safety">
          <h2>First, are you safe?</h2>
          <ul className="safety-list">
            <li><b>If anyone is injured, call 911 now.</b></li>
            <li>Move to a safe spot and turn on hazard lights if you can.</li>
            <li>Exchange info and photograph the scene and damage.</li>
            <li>You can come back and finish this pricing guide anytime.</li>
          </ul>

          <label className="consent">
            <input
              type="checkbox"
              checked={consented}
              onChange={(e) => setConsented(e.target.checked)}
            />
            <span>
              I understand this is an <b>educational pricing guide</b>, not a repair
              quote, insurance, or legal advice, and agree that my answers are
              used to create my report, and that my answers are stored for up
              to ~60 days, then deleted.
            </span>
          </label>
          <details className="privacy">
            <summary>What happens to my data?</summary>
            <ul>
              <li>Your answers are used only to generate your pricing guide and its shareable report page.</li>
              <li>Your pricing guide is generated entirely by our own rules engine from published repair-cost data. Your answers are never sent to an AI service or any other third party.</li>
              <li>We never ask for or store photos of your vehicle.</li>
              <li>Your report becomes a public web page at an unguessable link, so anyone you share it with can view it. It contains no name or contact info.</li>
              <li>Everything we store auto-deletes about 60 days after your session starts.</li>
              <li>Nothing is sent to any shop or insurer unless you share the link yourself.</li>
              <li>Contact details are optional and never required for a pricing guide.</li>
              <li>The shop map loads tiles from OpenStreetMap, so your approximate map area is visible to that service (as with any online map).</li>
            </ul>
          </details>

          <div className="nav">
            <button className="ghost" onClick={() => setScreen(prefill ? "finder" : "landing")}>Back</button>
            <button className="cta" onClick={() => setScreen("wizard")} disabled={!consented}>
              I'm safe, continue
            </button>
          </div>
        </section>
      )}

      {screen === "wizard" && (
        <Wizard
          prefill={prefill}
          onExit={() => setScreen("landing")}
          onUnresumable={(reason) => {
            setResume(null);
            setNotice(
              reason === "finished"
                ? "That pricing guide was already finished. Its report link is below."
                : "Your saved pricing guide expired, so you'll need to start a new one.",
            );
            setScreen("landing");
          }}
          resume={resume}
        />
      )}
    </main>
  );
}
